/* ============================================================
 * 招聘模块 store
 *
 * 设计：
 *   - 只管「招聘岗位字典」这一份低频数据（岗位类型/学历/经验/城市）。
 *     列表、详情等业务数据不进 store，按页面 useAsyncData 模式取，
 *     避免像 order store 那样积一个越用越大的缓存 Map。
 *   - 缓存走 `utils/dict-cache.ts` 的 sessionStorage 方案（10 分钟软过期），
 *     与工单 / 入驻字典保持同一套实现，不另造轮子。
 *   - ⚠️ **不要在 onBeforeUnmount 里清缓存**：
 *     工单 / 公告模块踩过这个坑 —— 清了缓存导致每次切页都重拉。
 *     字典是跨页面共享的（广场筛选、发布表单、简历学历都要用），必须留在 store 里。
 *   - ⚠️ **不要挂进 `stores/reset-all.ts`**：
 *     登出时清理的是「用户态」数据；本 store 是全平台公开的参考数据（跟 home 一样），
 *     跨账号可共享。写在这里只是防止以后有人顺手把它加进 resetAllUserStores。
 * ============================================================ */

import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { getJobDict } from '@/api/job'
import { readJobDict, removeJobDict, writeJobDict } from '@/utils/dict-cache'
import type { DictOption, JobDictVO } from '@/types/job'

export const useJobStore = defineStore('job', () => {
  const dict = ref<JobDictVO | null>(null)
  const loading = ref(false)
  const errorMessage = ref('')
  /** 本次会话是否已经成功拉过（用于避免并发重复请求）。 */
  const fetched = ref(false)

  /**
   * 拉取字典（带 sessionStorage 缓存）。
   * @param force 忽略缓存强制重拉（后端加新枚举时可手动触发）。
   */
  async function loadDict(force = false): Promise<JobDictVO | null> {
    if (loading.value) return dict.value
    // 内存里已有且不是强制刷新，直接复用
    if (dict.value && !force) return dict.value

    if (!force) {
      const cached = readJobDict()
      if (cached) {
        dict.value = cached
        fetched.value = true
        return cached
      }
    }

    loading.value = true
    errorMessage.value = ''
    try {
      const resp = await getJobDict()
      dict.value = resp.data
      fetched.value = true
      writeJobDict(resp.data)
      return resp.data
    } catch (err) {
      errorMessage.value = err instanceof Error ? err.message : '加载字典失败'
      // 拉取失败但内存/缓存里有旧的 → 用旧的兜底，不让页面崩
      return dict.value ?? readJobDict()
    } finally {
      loading.value = false
    }
  }

  /* ============================================================
   * 四个下拉的便捷出口（页面直接 v-for 用）
   *   ⚠️ 文案一律用后端 label，不要在前端硬编码
   *      （实测：经验下�� FRESH 的真实 label 是「应届生」）
   * ============================================================ */

  /** 岗位类型：全职 / 兼职 / 实习 / 远程 */
  const jobTypeOptions = computed<DictOption[]>(() => dict.value?.jobTypeList ?? [])

  /** 学历要求：不限 / 大专 / 本科 / 硕士 / 博士 */
  const eduOptions = computed<DictOption[]>(() => dict.value?.eduList ?? [])

  /** 经验要求：不限 / 应届生 / 1年以内 / 1-3年 / 3-5年 / 5年以上 */
  const expOptions = computed<DictOption[]>(() => dict.value?.expList ?? [])

  /** 工作城市：一线+新一线 19 城 + 其他（value === label，中文） */
  const cityOptions = computed<DictOption[]>(() => dict.value?.cityList ?? [])

  /** 按 value 反查 label（找不到原样返回 value）。 */
  function labelOf(options: DictOption[], value: string | null | undefined): string {
    if (!value) return ''
    return options.find((o) => o.value === value)?.label ?? value
  }

  const jobTypeLabel = (value: string | null | undefined) => labelOf(jobTypeOptions.value, value)
  const eduLabel = (value: string | null | undefined) => labelOf(eduOptions.value, value)
  const expLabel = (value: string | null | undefined) => labelOf(expOptions.value, value)
  const cityLabel = (value: string | null | undefined) => labelOf(cityOptions.value, value)

  /** 清空字典（退出登录 / 登出重置时调用）。 */
  function reset(): void {
    dict.value = null
    loading.value = false
    errorMessage.value = ''
    fetched.value = false
    removeJobDict()
  }

  return {
    dict,
    loading,
    errorMessage,
    fetched,
    jobTypeOptions,
    eduOptions,
    expOptions,
    cityOptions,
    jobTypeLabel,
    eduLabel,
    expLabel,
    cityLabel,
    loadDict,
    reset,
  }
})
